import React, { useState } from 'react';
import CustomSelect from '../CustomSelect';

export default function DemographicsScreen({ items, demographics, setDemographic, onContinue, onBack, onOpenConsentModal }) {
  const [consentGiven, setConsentGiven] = useState(false);

  const allRequiredFilled = items
    .filter(item => item.required)
    .every(item => demographics[item.field_name]);

  const canContinue = allRequiredFilled && consentGiven;

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canContinue) {
        onContinue();
      }
    }
  }

  return (
    <div
      className="view on demographics-screen"
      data-testid="demographics-screen"
      style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
      onKeyDown={handleKeyDown}
    >
      {/* Scrollable content area */}
      <div className="demo-scroll-area" style={{
        flex: 1,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '16px',
        paddingRight: '12px',
      }}>
        <h1 data-content="survey.section_demographics">About You</h1>
        <p className="desc">Help us contextualise your results.</p>

        {items.map(item => {
          if (item.field_type === 'singleselect' && item.options && item.options.length <= 6) {
            return (
              <div className="input-group" key={item.item_id}>
                <div className="label teal">{item.question_text}</div>
                <div className="pill-grid">
                  {item.options.map(opt => (
                    <button
                      key={opt.option_id}
                      className={`pill ${demographics[item.field_name] === opt.option_id ? 'selected' : ''}`}
                      onClick={() => setDemographic(item.field_name, opt.option_id)}
                      data-testid={`pill-${item.field_name}-${opt.option_id}`}
                    >
                      {opt.option_text}
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          if (item.field_type === 'singleselect') {
            const selectOptions = item.options.map(opt => ({
              value: opt.option_id,
              label: opt.option_text,
            }));
            return (
              <div className="input-group" key={item.item_id}>
                <div className="label teal">{item.question_text}</div>
                <CustomSelect
                  options={selectOptions}
                  value={demographics[item.field_name] || ''}
                  onChange={val => setDemographic(item.field_name, val)}
                  placeholder="Select..."
                  testId={`select-${item.field_name}`}
                />
              </div>
            );
          }

          return null;
        })}

        {/* Consent checkbox — inside scrollable area */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginTop: '24px' }}>
          <input
            type="checkbox"
            id="consent-check"
            checked={consentGiven}
            onChange={e => setConsentGiven(e.target.checked)}
            data-testid="consent-checkbox"
            style={{
              width: '20px',
              height: '20px',
              marginTop: '3px',
              flexShrink: 0,
              accentColor: 'var(--teal)',
              cursor: 'pointer',
            }}
          />
          <div style={{ fontSize: 'var(--fs-small, 0.85rem)', color: 'var(--tx2)', lineHeight: '1.5' }}>
            <label
              htmlFor="consent-check"
              style={{ cursor: 'pointer' }}
              data-content="_misc.consent_text"
            >
              I understand that my responses will be used to generate a personalised AI readiness assessment. My data will be kept confidential and used only for this purpose.
            </label>
            {' '}
            <span
              onClick={() => onOpenConsentModal()}
              style={{ color: 'var(--teal)', textDecoration: 'underline', cursor: 'pointer' }}
              data-testid="consent-link"
              data-content="_misc.consent_link_text"
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') onOpenConsentModal(); }}
            >
              Read full privacy notice
            </span>
          </div>
        </div>
      </div>

      {/* Fixed footer — outside scroll area */}
      <div style={{ flexShrink: 0, paddingTop: '16px', paddingBottom: '16px' }}>
        <button
          className="btn btn-p"
          onClick={onContinue}
          disabled={!canContinue}
          data-testid="demographics-continue-btn"
          style={{
            width: '100%',
            opacity: canContinue ? 1 : 0.4,
            cursor: canContinue ? 'pointer' : 'not-allowed',
          }}
        >
          Continue
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
          <button className="text-link" onClick={onBack} data-testid="demographics-back-btn">
            &larr; Back
          </button>
        </div>
      </div>
    </div>
  );
}
